'use client';

import React from 'react';
import { motion } from 'framer-motion';
import styles from './ContentSection.module.scss';

export interface ContentSectionProps {
  id?: string;
  title?: string;
  subtitle?: string;
  children: React.ReactNode;
  variant?: 'default' | 'highlight' | 'alternate';
  backgroundColor?: 'default' | 'secondary' | 'tertiary';
  spacing?: 'compact' | 'normal' | 'spacious';
  animate?: boolean;
  className?: string;
}

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.2,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: 'easeOut' as const,
    },
  },
};

export function ContentSection({
  id,
  title,
  subtitle,
  children,
  variant = 'default',
  backgroundColor = 'default',
  spacing = 'normal',
  animate = true,
  className,
}: ContentSectionProps) {
  const sectionClasses = [
    styles.contentSection,
    styles[variant],
    styles[`bg-${backgroundColor}`],
    styles[`spacing-${spacing}`],
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const content = (
    <>
      {(title || subtitle) && (
        <header className={styles.header}>
          {title && <h2 className={styles.title}>{title}</h2>}
          {subtitle && <p className={styles.subtitle}>{subtitle}</p>}
        </header>
      )}
      <div className={styles.content}>{children}</div>
    </>
  );

  if (animate) {
    return (
      <motion.section
        id={id}
        className={sectionClasses}
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: '-100px' }}
      >
        <motion.div className={styles.inner} variants={itemVariants}>
          {content}
        </motion.div>
      </motion.section>
    );
  }

  return (
    <section id={id} className={sectionClasses}>
      <div className={styles.inner}>{content}</div>
    </section>
  );
}

export default ContentSection;